import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Server } from 'lucide-react'
import { catalogApi, type ClientDetail, type ClientEnvironment, type EnvironmentInput } from '@/api/catalogApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { Field, NativeSelect } from '@/components/common/Field'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { FormError, StatusPill, TextAction, ValidatedBadge, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { useCatalogMutation, useCatalogProducts } from '@/hooks/useCatalog'
import { EMPTY_ENV_FORM, ENV_KINDS, envToForm, environmentPayload, groupByProduct, validateEnvironment, type EnvironmentForm } from '@/lib/catalogForms'

/** A client's per-product environments (Prod / Validation / Test / Training), grouped by product. */
export function EnvironmentsPanel({ client }: { client: ClientDetail }) {
  const { t } = useTranslation()
  const [editing, setEditing] = useState<ClientEnvironment | 'new' | null>(null)
  const [confirm, setConfirm] = useState<{ env: ClientEnvironment; action: 'retire' | 'delete' } | null>(null)
  const save = useCatalogMutation(({ id, input }: { id?: string; input: EnvironmentInput }) =>
    id ? catalogApi.updateEnvironment(client.id, id, input) : catalogApi.addEnvironment(client.id, input),
  )
  const act = useCatalogMutation(({ env, action }: { env: ClientEnvironment; action: 'retire' | 'delete' | 'reactivate' }) =>
    action === 'delete'
      ? catalogApi.removeEnvironment(client.id, env.id).then(() => null)
      : catalogApi.updateEnvironment(client.id, env.id, { status: action === 'retire' ? 'retired' : 'active' }),
  )
  const groups = groupByProduct(client.environments)

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-end">
        <Button onClick={() => setEditing('new')}>{t('catalog.environments.add')}</Button>
      </div>
      {act.error && !confirm ? <FormError error={act.error} /> : null}
      {groups.length === 0 ? (
        <EmptyState compact icon={Server} message={t('catalog.environments.empty')} />
      ) : (
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-surface-subtle">
            <tr>
              <th className={`${thClass} w-40`}>{t('catalog.environments.cols.product')}</th>
              <th className={thClass}>{t('catalog.environments.cols.name')}</th>
              <th className={`${thClass} w-28`}>{t('catalog.environments.cols.kind')}</th>
              <th className={`${thClass} w-32`}>{t('catalog.environments.cols.validated')}</th>
              <th className={thClass}>{t('catalog.environments.cols.baseUrl')}</th>
              <th className={`${thClass} w-20`}>{t('catalog.environments.cols.tenants')}</th>
              <th className={`${thClass} w-24`}>{t('catalog.environments.cols.status')}</th>
              <th className={`${thClass} w-[180px]`} />
            </tr>
          </thead>
          {groups.map((g) => (
            <tbody key={g.product.id}>
              {g.rows.map((e, i) => (
                <tr key={e.id} className="border-b border-line">
                  <td className={`${tdClass} font-medium text-ink-strong`}>{i === 0 ? g.product.name : ''}</td>
                  <td className={`${tdClass} text-ink-strong`}>{e.name}</td>
                  <td className={tdClass}>{t(`catalog.kinds.${e.kind}`)}</td>
                  <td className={tdClass}>
                    <ValidatedBadge validated={e.isValidated} />
                  </td>
                  <td className={`${tdClass} break-all font-mono text-label`}>{e.baseUrl ?? '—'}</td>
                  <td className={`${tdClass} font-mono`}>{e._count.tenantLinks}</td>
                  <td className={tdClass}>
                    <StatusPill status={e.status} />
                  </td>
                  <td className={tdClass}>
                    <div className="flex justify-end gap-3">
                      <TextAction onClick={() => setEditing(e)}>{t('common.edit')}</TextAction>
                      {e.status === 'active' ? (
                        <TextAction onClick={() => setConfirm({ env: e, action: 'retire' })}>{t('catalog.retire')}</TextAction>
                      ) : (
                        <TextAction onClick={() => act.mutate({ env: e, action: 'reactivate' })}>{t('catalog.reactivate')}</TextAction>
                      )}
                      <TextAction
                        danger
                        disabled={e._count.tickets > 0 || e._count.tenantLinks > 0}
                        onClick={() => setConfirm({ env: e, action: 'delete' })}
                      >
                        {t('common.delete')}
                      </TextAction>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          ))}
        </table>
      )}

      {editing ? (
        <EnvironmentDialog
          env={editing === 'new' ? null : editing}
          pending={save.isPending}
          error={save.error}
          onClose={() => {
            setEditing(null)
            save.reset()
          }}
          onSave={(input) => save.mutate({ id: editing === 'new' ? undefined : editing.id, input }, { onSuccess: () => setEditing(null) })}
        />
      ) : null}

      <ConfirmDialog
        open={confirm !== null}
        destructive={confirm?.action === 'delete'}
        title={t(confirm?.action === 'delete' ? 'catalog.environments.deleteTitle' : 'catalog.environments.retireTitle', {
          name: confirm ? `${confirm.env.product.name} · ${confirm.env.name}` : '',
        })}
        body={t(confirm?.action === 'delete' ? 'catalog.environments.deleteBody' : 'catalog.environments.retireBody')}
        confirmLabel={confirm?.action === 'delete' ? t('common.delete') : t('catalog.retire')}
        pending={act.isPending}
        error={act.error ? errorMessage(act.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setConfirm(null)
            act.reset()
          }
        }}
        onConfirm={() => confirm && act.mutate(confirm, { onSuccess: () => setConfirm(null) })}
      />
    </div>
  )
}

function EnvironmentDialog({
  env,
  pending,
  error,
  onClose,
  onSave,
}: {
  env: ClientEnvironment | null
  pending: boolean
  error: unknown
  onClose: () => void
  onSave: (input: EnvironmentInput) => void
}) {
  const { t } = useTranslation()
  const products = useCatalogProducts()
  const [form, setForm] = useState<EnvironmentForm>(() => (env ? envToForm(env) : EMPTY_ENV_FORM))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)
  const options = (products.data ?? []).filter((p) => p.active || p.id === env?.productId)

  function submit(e: FormEvent) {
    e.preventDefault()
    const found = validateEnvironment(form) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0) onSave(environmentPayload(form, !!env))
  }

  return (
    <Dialog open onOpenChange={(o) => (o ? undefined : onClose())}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{env ? t('catalog.environments.editTitle') : t('catalog.environments.createTitle')}</DialogTitle>
        </DialogHeader>
        <form className="grid grid-cols-2 gap-4" onSubmit={submit} noValidate>
          <Field id="env-product" label={t('catalog.environments.product')} error={err('productId')} help={env ? undefined : t('catalog.environments.productHelp')} className="col-span-2">
            <NativeSelect id="env-product" value={form.productId} disabled={!!env} onChange={(e) => setForm({ ...form, productId: e.target.value })}>
              <option value="">{t('common.select')}</option>
              {options.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="env-name" label={t('catalog.environments.name')} error={err('name')}>
            <Input id="env-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field id="env-kind" label={t('catalog.environments.kind')}>
            <NativeSelect
              id="env-kind"
              value={form.kind}
              onChange={(e) => {
                const kind = e.target.value as EnvironmentForm['kind']
                setForm({ ...form, kind, ...(env ? {} : { isValidated: kind === 'production' || kind === 'validation' }) })
              }}
            >
              {ENV_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`catalog.kinds.${k}`)}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field id="env-url" label={t('catalog.environments.baseUrl')} error={err('baseUrl')} className="col-span-2">
            <Input id="env-url" className="font-mono" placeholder="https://" value={form.baseUrl} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} />
          </Field>
          <label className="col-span-2 flex items-center gap-2 text-meta text-ink">
            <input type="checkbox" checked={form.isValidated} onChange={(e) => setForm({ ...form, isValidated: e.target.checked })} />
            {t('catalog.environments.isValidated')}
          </label>
          <FormError error={error} />
          <DialogFooter className="col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
